import { AnimatePresence, motion } from 'framer-motion';
import { CircleAlert, Eye, EyeOff, LogOut, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../hooks/useAuth';
import { usePublisher } from '../../hooks/usePublisher';
import { useTRPC } from '../../hooks/useTRPC';
import { getErrorMessage } from '../../utils/errorHandling';
import { captureAndReportError } from '../../utils/errorReporting';
import { Button } from '../Button';
import { FieldFeedback, FieldLabel } from '../Form/FieldLabel';
import { Input } from '../Input';
import { EASE_OUT, InitialsAvatar, StatusPill } from '../portal';
import { SettingsPage, SettingsRailCard, SettingsSection } from '../settings';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../ui/dialog';
import { VerificationCodeInput } from '../VerificationCodeInput';

interface ProfileFormData {
  email: string;
  fullName: string;
  password: string;
  confirmPassword: string;
}

interface FormErrors {
  password?: string;
  confirmPassword?: string;
}

function PasswordVisibilityToggle({
  visible,
  onToggle,
}: {
  visible: boolean;
  onToggle: () => void;
}) {
  const label = visible ? 'Hide password' : 'Show password';
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={label}
      title={label}
      className="absolute right-3 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-seeker-400 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
    >
      {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
    </button>
  );
}

export const ProfileSettingsPage = () => {
  const [authState, { refreshUser, signOut }] = useAuth();
  const { activePublisher } = usePublisher();
  const trpc = useTRPC();
  const [formData, setFormData] = useState<ProfileFormData>({
    email: '',
    fullName: '',
    password: '',
    confirmPassword: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');

  useEffect(() => {
    if (authState.type === 'Authenticated') {
      setFormData({
        email: authState.user.email,
        fullName: authState.user.fullName,
        password: '',
        confirmPassword: '',
      });
    }
  }, [authState]);

  useEffect(() => {
    if (authState.type === 'Authenticated') {
      const fullNameChanged = formData.fullName !== authState.user.fullName;
      const passwordEntered =
        formData.password.length > 0 || formData.confirmPassword.length > 0;
      setHasChanges(fullNameChanged || passwordEntered);
    }
  }, [formData, authState]);

  const handleInputChange =
    (field: keyof ProfileFormData) => (value: string) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      if (field === 'password' || field === 'confirmPassword') {
        setFormErrors((prev) => ({
          ...prev,
          [field]: undefined,
        }));
      }
    };

  const validatePasswordFields = () => {
    const errors: FormErrors = {};

    if (formData.password || formData.confirmPassword) {
      if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters long';
      }

      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!hasChanges) {
      return;
    }

    setIsLoading(true);

    try {
      if (formData.password || formData.confirmPassword) {
        if (!validatePasswordFields()) {
          setIsLoading(false);
          return;
        }

        await trpc.initiatePasswordChange.mutate({
          newPassword: formData.password,
          confirmPassword: formData.confirmPassword,
        });

        setIsVerificationModalOpen(true);
        setIsLoading(false);
        return;
      }

      if (
        authState.type === 'Authenticated' &&
        formData.fullName !== authState.user.fullName
      ) {
        await trpc.updateUser.mutate({ fullName: formData.fullName });

        await refreshUser();

        toast.success('Profile updated successfully');
      } else {
        toast.info('No changes to save');
      }
    } catch (error: unknown) {
      captureAndReportError('Failed to save profile:', error);
      toast.error(
        getErrorMessage(error, 'Failed to update profile. Please try again.'),
      );
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 100);
    }
  };

  const closeVerificationModal = () => {
    setIsVerificationModalOpen(false);
    setVerificationCode('');
  };

  const handleVerifyPasswordChange = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit verification code');
      return;
    }

    setIsLoading(true);

    try {
      await trpc.verifyPasswordChange.mutate({
        code: verificationCode,
      });

      setIsVerificationModalOpen(false);

      setFormData((prev) => ({ ...prev, password: '', confirmPassword: '' }));
      setVerificationCode('');

      await refreshUser();

      toast.success('Password changed successfully');
    } catch (error: unknown) {
      captureAndReportError('Failed to verify password change:', error);
      toast.error(
        getErrorMessage(
          error,
          'Failed to verify password change. Please try again.',
        ),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading && hasChanges) {
      handleSave();
    }
  };

  // Same call and error handling as the publisher menu's Sign Out item.
  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      captureAndReportError('Sign out failed:', error);
    }
  };

  const emailVerified =
    authState.type === 'Authenticated' ? authState.user.emailVerified : null;

  return (
    <>
      <SettingsPage
        icon={<User />}
        title="Profile"
        description="Your personal account details."
        actions={
          <Button
            variant="primary"
            className="w-full min-w-[140px] sm:w-auto"
            onClick={handleSave}
            disabled={!hasChanges || isLoading}
            submitting={isLoading}
          >
            Save Changes
          </Button>
        }
        rail={
          <SettingsRailCard title="Signed in as">
            <div className="flex min-w-0 items-center gap-3">
              <InitialsAvatar name={formData.fullName} size={40} />
              <div className="flex min-w-0 flex-col">
                <span
                  className="truncate text-sm font-bold leading-5 text-white"
                  title={formData.fullName}
                >
                  {formData.fullName}
                </span>
                <span
                  className="truncate text-xs leading-4"
                  title={formData.email}
                >
                  {formData.email}
                </span>
              </div>
            </div>

            {activePublisher ? (
              <div className="flex min-w-0 items-center justify-between gap-3 border-t border-seeker-800 pt-3">
                <span
                  className="min-w-0 truncate text-sm leading-5 text-white"
                  title={activePublisher.name}
                >
                  {activePublisher.name}
                </span>
                {activePublisher.role ? (
                  <StatusPill tone="neutral">{activePublisher.role}</StatusPill>
                ) : null}
              </div>
            ) : null}

            <Button
              variant="ghostMuted"
              className="w-full justify-center"
              onClick={handleSignOut}
            >
              <LogOut className="size-4" />
              Sign out
            </Button>
          </SettingsRailCard>
        }
      >
        <SettingsSection
          title="Account"
          action={
            emailVerified !== null ? (
              <StatusPill tone={emailVerified ? 'published' : 'unpublished'}>
                {emailVerified ? 'Verified' : 'Unverified'}
              </StatusPill>
            ) : null
          }
        >
          <div className="flex flex-col gap-2">
            <FieldLabel label="Email Address" />
            <Input
              type="email"
              aria-label="Email Address"
              value={formData.email}
              onChange={(e) => handleInputChange('email')(e.target.value)}
              readOnly
              disabled={isLoading}
            />
          </div>

          <div className="flex flex-col gap-2">
            <FieldLabel label="Full Name" />
            <Input
              type="text"
              aria-label="Full Name"
              value={formData.fullName}
              onChange={(e) => handleInputChange('fullName')(e.target.value)}
              disabled={isLoading}
              onKeyDown={handleKeyDown}
            />
          </div>
        </SettingsSection>

        <SettingsSection
          title="Password"
          description="Changing your password sends a 6-digit code to your email."
        >
          <div className="flex flex-col gap-2">
            <FieldLabel
              label="New Password"
              explanation="Leave blank to keep your current password."
            />
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                aria-label="New Password"
                value={formData.password}
                onChange={(e) => handleInputChange('password')(e.target.value)}
                className={`pr-12 ${formErrors.password ? 'border-orange-400 hover:border-orange-300 focus:border-orange-400' : ''}`}
                placeholder="••••••••"
                disabled={isLoading}
                onKeyDown={handleKeyDown}
              />
              <PasswordVisibilityToggle
                visible={showPassword}
                onToggle={() => setShowPassword((v) => !v)}
              />
            </div>
            {formErrors.password ? (
              <FieldFeedback className="motion-enter justify-start">
                <CircleAlert aria-hidden="true" />
                {formErrors.password}
              </FieldFeedback>
            ) : null}
          </div>

          {/* `empty:hidden` drops the flex slot (and its gap) once the field has collapsed. */}
          <div className="empty:hidden">
            <AnimatePresence initial={false}>
              {(formData.password || formData.confirmPassword) && (
                <motion.div
                  key="confirm-password"
                  initial={{ height: 0, opacity: 0, overflow: 'hidden' }}
                  animate={{
                    height: 'auto',
                    opacity: 1,
                    transition: { duration: 0.18, ease: EASE_OUT },
                    transitionEnd: { overflow: 'visible' },
                  }}
                  exit={{
                    height: 0,
                    opacity: 0,
                    overflow: 'hidden',
                    transition: { duration: 0.12, ease: EASE_OUT },
                  }}
                  className="flex flex-col gap-2"
                >
                  <FieldLabel label="Confirm Password" />
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      aria-label="Confirm Password"
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        handleInputChange('confirmPassword')(e.target.value)
                      }
                      className={`pr-12 ${formErrors.confirmPassword ? 'border-orange-400 hover:border-orange-300 focus:border-orange-400' : ''}`}
                      placeholder="••••••••"
                      disabled={isLoading}
                      onKeyDown={handleKeyDown}
                    />
                    <PasswordVisibilityToggle
                      visible={showConfirmPassword}
                      onToggle={() => setShowConfirmPassword((v) => !v)}
                    />
                  </div>
                  {formErrors.confirmPassword ? (
                    <FieldFeedback className="motion-enter justify-start">
                      <CircleAlert aria-hidden="true" />
                      {formErrors.confirmPassword}
                    </FieldFeedback>
                  ) : null}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </SettingsSection>
      </SettingsPage>

      <Dialog
        open={isVerificationModalOpen}
        onOpenChange={(open) => {
          if (!open && !isLoading) {
            closeVerificationModal();
          }
        }}
      >
        <DialogContent className="max-w-md gap-6 rounded-xl border-seeker-800 bg-seeker-900 p-6 [&>button:last-child]:hidden">
          <div className="flex flex-col gap-1">
            <DialogTitle className="text-xl font-semibold leading-7">
              Verify Password Change
            </DialogTitle>
            <DialogDescription className="text-sm text-seeker-400">
              We&apos;ve sent a verification code to your email address. Enter
              the 6-digit code below to confirm your password change.
            </DialogDescription>
          </div>

          <div className="flex flex-col gap-2">
            <FieldLabel label="Verification Code" />
            <VerificationCodeInput
              value={verificationCode}
              onChange={setVerificationCode}
              onEnter={() => {
                if (verificationCode.length === 6) {
                  handleVerifyPasswordChange();
                }
              }}
              className="justify-start"
              disabled={isLoading}
              autoFocus
            />
          </div>

          <div className="flex flex-col-reverse gap-3 sm:flex-row">
            <Button
              variant="outlineMuted"
              className="w-full sm:w-[120px]"
              onClick={closeVerificationModal}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              className="w-full sm:w-[120px]"
              onClick={handleVerifyPasswordChange}
              disabled={isLoading || verificationCode.length !== 6}
              submitting={isLoading}
            >
              Verify
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
